import { useState } from 'react';
import { CaretLeftIcon, CaretRightIcon, XIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';

import { IconButton } from '@/ui/button/Button';

/**
 * Thumbnails that open full size in an in-app dialog.
 *
 * Thumbnails are unreadable for screenshots, which is most of what gets attached
 * here, so the full image has to be reachable — but in the session rather than a
 * new tab, since a data URL there loses all app context.
 */
export function ImageGallery({
  images,
  variant = 'attachment',
}: {
  images: string[];
  /**
   * `attachment` is a user's own upload, sitting right-aligned in their bubble as a square.
   * `screenshot` is one the agent took: left-aligned inside a tool card, and wide with the aspect
   * ratio kept — a square crop of a 1024x640 page throws away the layout, which is the only
   * reason to be looking at it.
   */
  variant?: 'attachment' | 'screenshot';
}) {
  const [openAt, setOpenAt] = useState<number | null>(null);

  if (images.length === 0) return null;

  const step = (delta: number) => setOpenAt((at) => (at === null ? at : (at + delta + images.length) % images.length));
  const screenshot = variant === 'screenshot';
  const noun = screenshot ? 'screenshot' : 'attachment';

  return (
    <div
      className={
        screenshot ? 'flex max-w-full flex-wrap justify-start gap-1.5' : 'flex max-w-135 flex-wrap justify-end gap-1.5'
      }
    >
      {images.map((image, i) => (
        <button
          key={image.slice(-32) + i}
          type="button"
          onClick={() => setOpenAt(i)}
          aria-label={`Open ${noun} ${i + 1}`}
          className="rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-border"
        >
          <img
            src={image}
            alt={`${screenshot ? 'Screenshot' : 'Attachment'} ${i + 1}`}
            className={
              screenshot
                ? 'max-h-64 max-w-full rounded-lg border border-line-subtle object-contain transition-opacity hover:opacity-80'
                : 'size-24 rounded-lg border border-line-subtle object-cover transition-opacity hover:opacity-80'
            }
          />
        </button>
      ))}
      <Dialog.Root open={openAt !== null} onOpenChange={(open) => !open && setOpenAt(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed inset-0 z-50 flex items-center justify-center p-8 animate-fade-in focus:outline-none"
            // Clicks land on the padded backdrop far more often than on the image itself.
            onClick={() => setOpenAt(null)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') step(1);
              if (e.key === 'ArrowLeft') step(-1);
            }}
          >
            <Dialog.Title className="sr-only">
              Attachment {(openAt ?? 0) + 1} of {images.length}
            </Dialog.Title>
            {openAt !== null && (
              <img
                src={images[openAt]}
                alt={`Attachment ${openAt + 1}`}
                onClick={(e) => e.stopPropagation()}
                className="max-h-full max-w-full rounded-xl border border-line-strong object-contain shadow-2xl"
              />
            )}
            {images.length > 1 && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-line bg-surface-2 px-2 py-1"
              >
                <IconButton onClick={() => step(-1)} aria-label="Previous attachment">
                  <CaretLeftIcon size={16} />
                </IconButton>
                <span className="text-2xs text-fg-tertiary tabular-nums">
                  {(openAt ?? 0) + 1} / {images.length}
                </span>
                <IconButton onClick={() => step(1)} aria-label="Next attachment">
                  <CaretRightIcon size={16} />
                </IconButton>
              </div>
            )}
            <Dialog.Close asChild>
              <IconButton className="absolute top-6 right-6" aria-label="Close attachment">
                <XIcon size={16} />
              </IconButton>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
